<template>
    <div class="views-content">
        <div class="view-selection-row">
            <div class="view-actions">
                <SelectView
                    :views="options"
                    :items="props.items"
                    :is-loading="isLoading"
                    :default-view="defaultView"
                    :before-change="selectBeforeChange"
                    @view-changed="handleViewChanged"
                    @open-share="handleOpenShare"
                />
            </div>
        </div>
        <UpdateViewControl
            :is-dirty="isDirty"
            @view-updated="handleViewUpdated"
        />
        <ShareViewModal ref="shareRef" />
    </div>
</template>
<script setup lang="ts">
import {
    defineEmits,
    defineProps,
    inject,
    ref,
    watch,
    PropType,
    nextTick
} from 'vue';
import type PagePreference from '@agyletime/vue-3-common/src/typings/pagePreference';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import SelectView from '@agyletime/vue-3-common/src/components/PagePreferenceSelector/SelectView.vue';
import ShareViewModal from '@agyletime/vue-3-common/src/components/PagePreferenceSelector/ShareView.vue';
import UpdateViewControl from '@agyletime/vue-3-common/src/components/PagePreferenceSelector/UpdateView.vue';
import { useExposedFunction } from '@agyletime/vue-3-common/src/composables/useExposedValue';
import { AnyFilter } from '@playvox/almendra/dist/components/filter/filterConfiguration';

const emits = defineEmits([
    'viewChanged',
    'viewUpdated',
    'loaded'
]);

const props = defineProps({
    isLoading: {
        type: Boolean,
        default: false
    },
    isDirty: {
        type: Boolean,
        default: false
    },
    defaultView: {
        type: Object as PropType<PagePreference | null>,
        default: () => null
    },
    items: {
        type: Array as PropType<AnyFilter[]>,
        required: true
    }
});

const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const selectBeforeChange = inject<(() => boolean) | undefined>('views:select:beforeChange', undefined);

const options = ref<PagePreference[]>([]);
const [ shareRef, openShare ] = useExposedFunction('openShare');

function handleViewChanged(view: PagePreference | null) {
    emits('viewChanged', view, false);
}

function handleOpenShare(view: PagePreference) {
    openShare(view);
}

function handleViewUpdated(view: PagePreference) {
    if (pagePreferences) {
        const idx = pagePreferences.preferences.value.findIndex((pref) => pref._id === view._id);
        const newArray = [...pagePreferences.preferences.value];
        newArray[idx] = view;
        pagePreferences.preferences.value = newArray;
    }
    emits('viewUpdated', view);
}

function mapPreferences(data: PagePreference[]) {
    return data
        ?.map((p) => {
            let ownership = 'Shared Views';
            if (p.isMine) {
                ownership = 'My Views';
            }
            if (p.isFavourite) {
                ownership = 'Default';
            }
    
            return { ...p, ownership };
        })
        .sort((a, b) => {
            return new Date(b.updatedAt ?? b.createdAt).getTime() - new Date(a.updatedAt ?? a.createdAt).getTime();
        });
}

const loaded = ref(false);
watch(
    () => [pagePreferences?.preferences.value],
    () => {
        // Placed at the top in case there's an error below to avoid infinite loading...
        if (!loaded.value) {
            nextTick(() => emits('loaded'));
            loaded.value = true;
        }

        const mapped = mapPreferences(pagePreferences?.preferences?.value ?? []) ;
        options.value = mapped;
    },
    { deep: true, immediate: true }
);
</script>
<style scoped lang="scss">
.views-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  .view-selection-row {
    display: flex;
    justify-content: flex-end;
  }

  .view-actions {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    align-items: end;
  }

  /** Remove right-aligned arrows */
  .multiselect__option {
    &::after {
      content: none !important;
    }

    &--selected {
      &.multiselect__option::after {
        content: none !important;
      }
    }

    .view-option {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      .view-actions {
        display: flex;
        flex-direction: row;
        gap: 8px;

        span.al2-icon {
          cursor: pointer;
          font-size: 22px;

          &:hover {
            color: #35495e;
          }
        }
      }
    }
  }

  .route-title.has-view-selected {
    font-size: 1.25em;
    color: var(--color-text--50)
  }

  .view-title, .route-title  {
    font-size: 2em;
    font-weight: bold;

    transition: font-size 0.3s;
  }

  .view-title:not(.has-view-selected) {
    font-size: 0;
  }

  .update-alert {
    margin-bottom: 0;
    margin-top: var(--spacing-2, 8px);

    .alert {
      margin-bottom: 0;
    }
  }
}
</style>
